import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
} from '@mui/icons-material';
import { AuthService } from '@/services/authService';
import { User, LoginCredentials } from '@/models/user';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import './LoginPage.css';

interface LoginPageProps {
  onLogin: (user: User, token: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [formData, setFormData] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validation
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (!formData.password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await AuthService.login(formData);

      
      if (response.success) {
        setSuccess('Login successful! Redirecting...');
        // Store the token first
        AuthService.storeToken(response.data.token);
        
        // Fetch user profile after successful login
        try {
          const profileResponse = await AuthService.getProfile();
          if (profileResponse.success) {
            const user = profileResponse.data;
            AuthService.storeAuthData(response.data.token, user);
            onLogin(user, response.data.token);
          } else {
            setError('Failed to fetch user profile');
          }
        } catch (profileError: any) {
          setError('Login successful but failed to fetch user profile');
          console.error('Profile fetch error:', profileError);
        }
      } else {
        setError(response.error || 'Login failed');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className="login-page">
      <Container maxWidth="sm" className="login-container">
        <Paper elevation={8} className="login-paper">
          <Box className="login-header">
            <Typography variant="h4" component="h1" className="login-title">
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" className="login-subtitle">
              Sign in to your account to continue
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} className="login-form">
            {error && (
              <Alert severity="error" className="login-alert">
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" className="login-alert">
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={formData.username}
              onChange={handleInputChange('username')}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              className="login-field"
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={formData.password}
              onChange={handleInputChange('password')}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="login-field"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <LoadingSpinner size={20} /> : <LoginIcon />}
              className="login-button"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>

          <Divider className="login-divider">
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Box className="login-footer">
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to="/register"
                variant="body2"
                className="login-link"
              >
                Sign up here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage; 